import React, { useMemo, useState } from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Eyebrow, Reveal } from "./_shared";
import { useTheme } from "../../theme/ThemeProvider";

const CATEGORIES = ["All", "News", "Articles", "Practice Highlights"];

const POSTS = [
  {
    featured: true,
    tag: "News",
    date: "Sept 2026",
    title:
      "Charis & Mettle advises on the transaction to acquire 100% of Africa's largest CNG producer.",
    excerpt:
      "The firm advised on the transaction to acquire 100% of the largest producer of Compressed Natural Gas in Africa.",
    url: "https://guardian.ng/news/ardova-led-consortium-to-acquire-100-of-africas-largest-cng-producer-powergas-2/",
    img:
      "https://customer-assets-jt897jd0.emergentagent.net/job_acquisition-legal/artifacts/58vl4cat_CNG%20Image.jpg",
  },
];

export default function Insights() {
  const { theme } = useTheme();
  const isBlakes = theme === "blakes";
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(() => {
    if (filter === "All") return POSTS;
    return POSTS.filter((p) => p.tag === filter);
  }, [filter]);

  const featured = filtered.find((p) => p.featured) || filtered[0];
  const rest = filtered.filter((p) => p !== featured);

  const accentText = isBlakes ? "text-ochre-500" : "text-sage-700";
  const hoverAccent = isBlakes ? "group-hover:text-ochre-500" : "group-hover:text-sage-700";
  const chipActive = isBlakes
    ? "bg-ochre-500 text-white border-ochre-500"
    : "bg-sage-600 text-white border-sage-600";
  const chipIdle = "bg-transparent text-charcoal border-cream-300 hover:border-charcoal";

  return (
    <section
      id="insights"
      data-testid="insights-section"
      className={`py-24 lg:py-32 border-y border-cream-300 ${isBlakes ? "bg-white" : "bg-cream-50"}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <Eyebrow>{isBlakes ? "Insights" : "News"}</Eyebrow>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[1.05]">
              News and
              <span className={`italic ${accentText}`}> Articles.</span>
            </h2>
          </div>
          <a
            href="#contact"
            className={`hidden lg:inline-flex items-center gap-2 text-charcoal font-sans text-[12px] uppercase tracking-[0.22em] transition-colors ${isBlakes ? "hover:text-ochre-500" : "hover:text-sage-700"}`}
            data-testid="insights-all-link"
          >
            All writing <ArrowRight size={16} />
          </a>
        </Reveal>

        {/* Category chips */}
        <Reveal delay={0.08} data-testid="insights-filter" className="flex flex-wrap gap-2 mb-10">
          {CATEGORIES.map((c) => {
            const active = filter === c;
            return (
              <button
                key={c}
                type="button"
                data-testid={`insights-filter-${c.replace(/\s+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 border font-sans text-[11px] uppercase tracking-[0.22em] transition-colors ${active ? chipActive : chipIdle}`}
              >
                {c}
              </button>
            );
          })}
        </Reveal>

        {featured && (
          <Reveal
            as="a"
            delay={0.12}
            href={featured.url || "#"}
            target={featured.url && featured.url !== "#" ? "_blank" : undefined}
            rel="noopener noreferrer"
            data-testid="insight-featured"
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 lg:mb-20 group cursor-pointer"
          >
            <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden bg-cream-200">
              <img
                src={featured.img}
                alt=""
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
              {/* Spotlight wash on hover */}
              <div
                aria-hidden="true"
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_30%_20%,rgba(249,248,246,0.22),transparent_60%)]"
              />
              <span
                className={`absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] text-white px-2.5 py-1 ${isBlakes ? "bg-ochre-500" : "bg-sage-700"}`}
              >
                Featured · {featured.tag}
              </span>
            </div>
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className={`flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-sans ${accentText}`}>
                <span>{featured.tag}</span>
                <span className="h-px w-6 bg-cream-300" />
                <span data-testid="insight-featured-date" className="text-charcoal-muted">{featured.date}</span>
              </div>
              <h3 className={`mt-4 font-serif text-3xl lg:text-[40px] text-charcoal leading-[1.1] transition-colors ${hoverAccent}`}>
                {featured.title}
              </h3>
              <p className="mt-5 text-charcoal-muted font-sans text-[15px] leading-relaxed max-w-xl">
                {featured.excerpt}
              </p>
              <span
                data-testid="insight-read-article"
                className={`mt-8 inline-flex items-center gap-2 text-charcoal font-sans text-[12px] uppercase tracking-[0.22em] transition-colors ${hoverAccent}`}
              >
                Read Article
                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </div>
          </Reveal>
        )}

        {rest.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
            {rest.slice(0, 3).map((p, i) => (
              <Reveal
                as="article"
                key={p.title}
                delay={i * 0.08}
                data-testid={`insight-card-${i}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-cream-200">
                  <img
                    src={p.img}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                  <span
                    className={`absolute top-3 left-3 text-[10px] uppercase tracking-[0.22em] text-white px-2 py-0.5 ${isBlakes ? "bg-ochre-500/95" : "bg-sage-700/90"}`}
                  >
                    {p.tag}
                  </span>
                </div>
                <div className={`mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] font-sans ${accentText}`}>
                  <span>{p.tag}</span>
                  <span className="h-px w-6 bg-cream-300" />
                  <span className="text-charcoal-muted">{p.date}</span>
                </div>
                <h3 className={`mt-3 font-serif text-2xl lg:text-[26px] text-charcoal leading-snug transition-colors ${hoverAccent}`}>
                  {p.title}
                </h3>
                <p className="mt-3 text-charcoal-muted font-sans text-[14px] leading-relaxed line-clamp-3">
                  {p.excerpt}
                </p>
                <span
                  className={`mt-5 inline-flex items-center gap-2 text-charcoal font-sans text-[12px] uppercase tracking-[0.22em] transition-colors ${hoverAccent}`}
                >
                  Read Article
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Reveal>
            ))}
          </div>
        )}

        {!featured && rest.length === 0 && (
          <p
            data-testid="insights-empty"
            className="text-charcoal-muted font-sans text-[15px] leading-relaxed"
          >
            No entries in this category yet — check back soon.
          </p>
        )}
      </div>
    </section>
  );
}
